<template>
  <div class="ChecklistShareTasksDialogRolePicker overflow-y-auto">
    <template v-if="loading">
      <v-row>
        <v-col class="text-center">
          <AppLoader/>
        </v-col>
      </v-row>
    </template>
    <template v-else>
      <div class="ChecklistShareTasksDialogRolePicker-headerRow px-4">
        <div></div>
        <h4 class="text-center">{{$t('project.checklist.dialogs.ChecklistShareTasksDialogRolePicker.responsible')}}</h4>
        <h4 class="text-center">{{$t('project.checklist.dialogs.ChecklistShareTasksDialogRolePicker.validators')}}</h4>
        <h4 class="text-center">{{$t('project.checklist.dialogs.ChecklistShareTasksDialogRolePicker.observers')}}</h4>
      </div>
      <ChecklistShareTasksDialogRolePickerGroup v-for="group in groups"
                                                :key="`group-${group.id}`"
                                                :mnemo="mnemo"
                                                :group="group"
                                                :taskVisibleByAll="taskVisibleByAll"
      />
    </template>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'

import AppLoader from '@/common/AppLoader.vue'
import { checklistTasksHaveSameRights } from '@/common/utils/checklist'
import { toast } from '@/common/utils/toast'
import ChecklistShareTasksDialogRolePickerGroup from '@/project/checklist/dialogs/ChecklistShareTasksDialogRolePickerGroup.vue'

import {
  ADD_SELECTED_OBSERVERS_GROUPS,
  ADD_SELECTED_OBSERVERS_MEMBERS,
  ADD_SELECTED_RESPONSIBLES_GROUPS,
  ADD_SELECTED_RESPONSIBLES_MEMBERS,
  ADD_SELECTED_VALIDATORS_GROUPS,
  REMOVE_SELECTED_OBSERVERS_GROUPS,
  ADD_SELECTED_VALIDATORS_MEMBERS,
  RESET_ROLE_PICKER,
} from '../../../store/modules/checklist/mutation_types'

export default {
  name: 'ChecklistShareTasksDialogRolePicker',
  components: { ChecklistShareTasksDialogRolePickerGroup, AppLoader },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
    taskVisibleByAll: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    ...mapState('checklist', ['shareTasksDialog']),
    ...mapState('groups', ['groups', 'loading']),
  },
  watch: {
    taskVisibleByAll (value) {
      this.onTaskVisibleByAllClick(value)
    },
  },
  async mounted () {
    try {
      await this.GET_GROUPS(this.mnemo)

      const pmGroup = this.groups.find(item => item.isPm)
      this.ADD_SELECTED_OBSERVERS_GROUPS(pmGroup)

      const everyTasksToShareHaveTheSameRights = checklistTasksHaveSameRights(this.shareTasksDialog.tasksToShare)

      if (everyTasksToShareHaveTheSameRights) {
        this.shareTasksDialog.tasksToShare[0].rights.observers.groups.forEach(item => {
          this.ADD_SELECTED_OBSERVERS_GROUPS(this.groups.find(o => o.id === item.id))
        })
        this.shareTasksDialog.tasksToShare[0].rights.observers.users.forEach(item => {
          this.ADD_SELECTED_OBSERVERS_MEMBERS(this.findUser(item))
        })
        this.shareTasksDialog.tasksToShare[0].rights.responsibles.groups.forEach(item => {
          this.ADD_SELECTED_RESPONSIBLES_GROUPS(this.groups.find(o => o.id === item.id))
        })
        this.shareTasksDialog.tasksToShare[0].rights.responsibles.users.forEach(item => {
          this.ADD_SELECTED_RESPONSIBLES_MEMBERS(this.findUser(item))
        })
        this.shareTasksDialog.tasksToShare[0].rights.validators.groups.forEach(item => {
          this.ADD_SELECTED_VALIDATORS_GROUPS(this.groups.find(o => o.id === item.id))
        })
        this.shareTasksDialog.tasksToShare[0].rights.validators.users.forEach(item => {
          this.ADD_SELECTED_VALIDATORS_MEMBERS(this.findUser(item))
        })
      }
      
      if (this.taskVisibleByAll) {
        this.onTaskVisibleByAllClick(true)
      }
    } catch (error) {
      console.error(error)
      toast.error(this.$t('project.checklist.dialogs.ChecklistShareTasksDialogRolePicker.getShareTaskRolePickerGroupsError'))
    }
  },
  beforeUnmount () {
    this.RESET_ROLE_PICKER()
  },
  methods: {
    ...mapActions('groups', ['GET_GROUPS']),
    ...mapMutations('checklist', [
      ADD_SELECTED_OBSERVERS_GROUPS,
      ADD_SELECTED_OBSERVERS_MEMBERS,
      ADD_SELECTED_RESPONSIBLES_GROUPS,
      ADD_SELECTED_RESPONSIBLES_MEMBERS,
      ADD_SELECTED_VALIDATORS_GROUPS,
      ADD_SELECTED_VALIDATORS_MEMBERS,
      REMOVE_SELECTED_OBSERVERS_GROUPS,
      RESET_ROLE_PICKER,
    ]),
    findUser (user) {
      if (this.groups && this.groups.length > 0) {
        for (const group of this.groups) {
          if (group.members.some(item => item.id === user.id)) {
            return group.members.find(item => item.id === user.id)
          }
        }
      }
    },
    onTaskVisibleByAllClick (visibleToAll) {
      for (const group of this.groups) {
        if (visibleToAll) {
          this.ADD_SELECTED_OBSERVERS_GROUPS(group)
        } else if (!group.isPm) {
          this.REMOVE_SELECTED_OBSERVERS_GROUPS(group)
        }
      }
    },
  },
}
</script>

<style scoped lang="scss">
.ChecklistShareTasksDialogRolePicker {
  max-height: 370px;
}
.ChecklistShareTasksDialogRolePicker-headerRow {
  display: grid;
  grid-template-columns: auto 180px 180px 180px;
  grid-gap: 10px;
}
</style>
